import { useState } from "react";
import { eventsApi } from "@/lib/api/endpoints";
import { Card, CardSection, Checkbox, Field, Input, Select, Textarea, useToast } from "@/components/ui";
import { fromDateTimeLocal, toDateTimeLocal } from "@/lib/utils/format";
import { useBuilder } from "../BuilderContext";
import type { EventStatus, EventType } from "@/lib/types/config";

// SERVICES moved to FlowTab — see the comment on the tab list in SiteBuilderPage.

/**
 * Event identity and availability.
 *
 * Booth tools and the participant step used to live here too. They moved to the
 * Flow tab: they decide how many screens a guest walks through, and that is a
 * judgement you cannot make from a list of checkboxes with no picture of the
 * resulting journey.
 *
 * Unlike every other tab, most of this lives on the Event record rather than in
 * the config blob — so it saves immediately instead of going through the draft.
 */
export function GeneralTab() {
  const { event, setEvent, draft, update, patchEvent, markPreviewStale } = useBuilder();
  const toast = useToast();
  const [saving, setSaving] = useState(false);

  async function patchRecord(patch: Parameters<typeof eventsApi.update>[1]) {
    setSaving(true);
    try {
      const updated = await eventsApi.update(event.id, patch);
      setEvent({ ...event, ...updated });
      markPreviewStale();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not save");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="flex flex-col gap-8">
      <CardSection
        eyebrow="Event"
        title="General information"
        description="These settings identify the event, control availability, and choose which booth tools this site should expose."
      >
        <Card className="grid gap-5 p-5 sm:grid-cols-2">
          <Field label="Event name" required>
            <Input
              defaultValue={event.name}
              disabled={saving}
              onBlur={(e) => e.target.value !== event.name && void patchRecord({ name: e.target.value })}
            />
          </Field>

          <Field label="Slug" hint="Changing this changes the booth's public link.">
            <Input
              className="font-mono"
              defaultValue={event.slug}
              disabled={saving}
              onBlur={(e) => e.target.value !== event.slug && void patchRecord({ slug: e.target.value })}
            />
          </Field>

          <Field label="Event type">
            <Select
              value={event.event_type}
              onChange={(e) => void patchRecord({ event_type: e.target.value as EventType })}
            >
              <option value="festival">Festival</option>
              <option value="sports">Sports</option>
              <option value="wedding">Wedding</option>
              <option value="corporate">Corporate</option>
              <option value="other">Other</option>
            </Select>
          </Field>

          <Field label="Status" hint="Only published events serve their booth link.">
            <Select
              value={event.status}
              onChange={(e) => void patchRecord({ status: e.target.value as EventStatus })}
            >
              <option value="draft">Draft</option>
              <option value="published">Published</option>
              <option value="archived">Archived</option>
            </Select>
          </Field>

          <Field label="Start date">
            <Input
              type="datetime-local"
              defaultValue={toDateTimeLocal(event.starts_at)}
              onBlur={(e) => void patchRecord({ starts_at: fromDateTimeLocal(e.target.value) })}
            />
          </Field>

          <Field label="End date">
            <Input
              type="datetime-local"
              defaultValue={toDateTimeLocal(event.ends_at)}
              onBlur={(e) => void patchRecord({ ends_at: fromDateTimeLocal(e.target.value) })}
            />
          </Field>

          <Field label="Description" className="sm:col-span-2">
            <Textarea
              placeholder="Internal event notes or public event context for prompts and setup."
              value={draft.event?.description ?? ""}
              onChange={(e) => patchEvent({ description: e.target.value })}
            />
          </Field>
        </Card>
      </CardSection>

      {/* Booth tools and the participant step moved to the Flow tab.
          They decide how many screens a guest walks through, and that decision
          is impossible to make well when the controls are split across two tabs
          and neither shows you the resulting journey. */}

      <CardSection eyebrow="Privacy" title="Gallery">
        <Card className="flex flex-col gap-4 p-5">
          <Checkbox
            checked={draft.event?.gallery?.public ?? false}
            onChange={(value) =>
              update((current) => ({
                ...current,
                event: { ...current.event, gallery: { ...current.event?.gallery, public: value } },
              }))
            }
            label="Public gallery"
            hint="Lets anyone with the booth link browse every photo from this event. Guest photos are private by default — only turn this on if attendees expect a shared wall."
          />
        </Card>
      </CardSection>

      <CardSection eyebrow="AI" title="Generation defaults">
        <Card className="grid gap-5 p-5">
          <Field label="Provider" hint="Falls back to your organization default when left blank.">
            <Select
              value={draft.event?.provider ?? ""}
              onChange={(e) => patchEvent({ provider: e.target.value || undefined })}
            >
              <option value="">Organization default</option>
              <option value="bfl">Black Forest Labs (flux)</option>
              <option value="openai">OpenAI</option>
              <option value="stability">Stability</option>
            </Select>
          </Field>

          <Field
            label="System prompt"
            hint="Prepended to every prompt for this event. Good for a house style — 'cinematic, 35mm film' — rather than scene-specific detail."
          >
            <Textarea
              rows={3}
              value={draft.event?.system_prompt ?? ""}
              onChange={(e) => patchEvent({ system_prompt: e.target.value || undefined })}
            />
          </Field>
        </Card>
      </CardSection>
    </div>
  );
}
